/**
 * CR107: useLayoutStudio Hook
 * 
 * Manages Layout Studio state - presets, activations, and CRUD operations.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type {
  LayoutPreset,
  LayoutActivation,
  LayoutActivationAudit,
  LayoutJson,
  ActivationEnvironment,
} from "@/lib/layout-studio/types";
import { DEFAULT_LAYOUT_JSON } from "@/lib/layout-studio/types";

interface UseLayoutStudioReturn {
  presets: LayoutPreset[];
  activations: LayoutActivation[];
  auditLog: LayoutActivationAudit[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  
  // CRUD
  createPreset: (name: string, description?: string, layoutJson?: LayoutJson) => Promise<LayoutPreset | null>;
  updatePreset: (id: string, updates: Partial<Pick<LayoutPreset, "name" | "description" | "layout_json" | "status">>) => Promise<boolean>;
  archivePreset: (id: string) => Promise<boolean>;
  clonePreset: (id: string, newName?: string) => Promise<LayoutPreset | null>;
  deletePreset: (id: string) => Promise<boolean>;
  
  // Activations
  activatePreset: (presetId: string | null, environment: ActivationEnvironment, notes?: string) => Promise<boolean>;
  rollback: (environment: ActivationEnvironment, notes?: string) => Promise<boolean>;
  
  // Helpers
  getActivePreset: (environment: ActivationEnvironment) => LayoutPreset | null;
  refetch: () => Promise<void>;
}

export function useLayoutStudio(): UseLayoutStudioReturn {
  const [presets, setPresets] = useState<LayoutPreset[]>([]);
  const [activations, setActivations] = useState<LayoutActivation[]>([]);
  const [auditLog, setAuditLog] = useState<LayoutActivationAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch presets
      const presetsData = await supabase.functions.invoke("preset-manager?endpoint=presets", {
        method: "GET",
      });
      
      if (presetsData.error) throw new Error(presetsData.error.message);
      setPresets((presetsData.data?.presets || []) as LayoutPreset[]);

      // Fetch activations
      const activationsData = await supabase.functions.invoke("preset-manager?endpoint=activations", {
        method: "GET",
      });
      
      if (activationsData.error) throw new Error(activationsData.error.message);
      setActivations((activationsData.data?.activations || []) as LayoutActivation[]);
      setAuditLog((activationsData.data?.auditLog || []) as LayoutActivationAudit[]);

    } catch (err) {
      console.error("[useLayoutStudio] Fetch error:", err);
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create preset
  const createPreset = useCallback(async (
    name: string,
    description?: string,
    layoutJson?: LayoutJson
  ): Promise<LayoutPreset | null> => {
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("preset-manager?action=create_preset", {
        method: "POST",
        body: {
          name,
          description,
          layout_json: layoutJson || DEFAULT_LAYOUT_JSON,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const newPreset = data.preset as LayoutPreset;
      setPresets((prev) => [newPreset, ...prev]);
      toast.success(`Preset "${name}" created`);
      return newPreset;
    } catch (err) {
      console.error("[useLayoutStudio] Create error:", err);
      toast.error("Failed to create preset");
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  // Update preset
  const updatePreset = useCallback(async (
    id: string,
    updates: Partial<Pick<LayoutPreset, "name" | "description" | "layout_json" | "status">>
  ): Promise<boolean> => {
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("preset-manager?action=update_preset", {
        method: "POST",
        body: { id, ...updates },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const updated = data.preset as LayoutPreset;
      setPresets((prev) => prev.map((p) => (p.id === id ? updated : p)));
      toast.success("Preset updated");
      return true;
    } catch (err) {
      console.error("[useLayoutStudio] Update error:", err);
      toast.error("Failed to update preset");
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Archive preset
  const archivePreset = useCallback(async (id: string): Promise<boolean> => {
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("preset-manager?action=archive_preset", {
        method: "POST",
        body: { id },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setPresets((prev) => prev.filter((p) => p.id !== id));
      toast.success("Preset archived");
      return true;
    } catch (err) {
      console.error("[useLayoutStudio] Archive error:", err);
      toast.error("Failed to archive preset");
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Clone preset
  const clonePreset = useCallback(async (id: string, newName?: string): Promise<LayoutPreset | null> => {
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("preset-manager?action=clone_preset", {
        method: "POST",
        body: { id, newName },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const cloned = data.preset as LayoutPreset;
      setPresets((prev) => [cloned, ...prev]);
      toast.success(`Preset cloned`);
      return cloned;
    } catch (err) {
      console.error("[useLayoutStudio] Clone error:", err);
      toast.error("Failed to clone preset");
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  // Delete preset
  const deletePreset = useCallback(async (id: string): Promise<boolean> => {
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("preset-manager?action=delete_preset", {
        method: "POST",
        body: { id },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setPresets((prev) => prev.filter((p) => p.id !== id));
      toast.success("Preset deleted");
      return true;
    } catch (err) {
      console.error("[useLayoutStudio] Delete error:", err);
      toast.error(String(err).includes("Cannot delete") ? String(err) : "Failed to delete preset");
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Activate preset
  const activatePreset = useCallback(async (
    presetId: string | null,
    environment: ActivationEnvironment,
    notes?: string
  ): Promise<boolean> => {
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("preset-manager?action=activate", {
        method: "POST",
        body: { preset_id: presetId, environment, notes },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      await fetchData(); // Refetch to update activations and audit
      toast.success(`Preset activated for ${environment}`);
      return true;
    } catch (err) {
      console.error("[useLayoutStudio] Activate error:", err);
      toast.error("Failed to activate preset");
      return false;
    } finally {
      setSaving(false);
    }
  }, [fetchData]);

  // Rollback
  const rollback = useCallback(async (
    environment: ActivationEnvironment,
    notes?: string
  ): Promise<boolean> => {
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("preset-manager?action=rollback", {
        method: "POST",
        body: { environment, notes },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      await fetchData();
      toast.success(`Rolled back ${environment}`);
      return true;
    } catch (err) {
      console.error("[useLayoutStudio] Rollback error:", err);
      toast.error("Failed to rollback");
      return false;
    } finally {
      setSaving(false);
    }
  }, [fetchData]);

  // Get active preset for environment
  const getActivePreset = useCallback((environment: ActivationEnvironment): LayoutPreset | null => {
    const activation = activations.find((a) => a.environment === environment);
    if (!activation?.active_preset_id) return null;
    return presets.find((p) => p.id === activation.active_preset_id) || null;
  }, [activations, presets]);

  return {
    presets,
    activations,
    auditLog,
    loading,
    saving,
    error,
    createPreset,
    updatePreset,
    archivePreset,
    clonePreset,
    deletePreset,
    activatePreset,
    rollback,
    getActivePreset,
    refetch: fetchData,
  };
}
